import { IDockviewPanelProps } from "dockview-react";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api } from "./utils/api";

const modules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image"],
  ],
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "indent",
  "link",
  "image",
];

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function KKCommentNoteEditor(props: IDockviewPanelProps) {
  const id = props.params?.id as number;
  const [content, setContent] = useState<string>("");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [isLoading, setIsLoading] = useState(true);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    api
      .get<{ note: string }>("/kk-comment-note", { params: { id } })
      .then((resp) => {
        setContent(resp.data.note ?? "");
      })
      .catch((err: any) => {
        if (err?.response?.status === 404) {
          // No note yet — start empty
          setContent("");
        } else {
          console.error("Failed to load note:", err);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const normalizeContent = (html: string): string => {
    const text = html.replace(/<[^>]*>/g, "").trim();
    return text.length === 0 ? "" : html;
  };

  const saveNote = async (noteContent: string) => {
    const normalized = normalizeContent(noteContent);
    setSaveStatus("saving");
    try {
      await api.put("/kk-comment-note", { data: normalized }, { params: { id } });
      setSaveStatus("saved");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
      window.dispatchEvent(
        new CustomEvent("kk-note-changed", { detail: { id, has_notes: normalized !== "" } })
      );
    } catch (err) {
      console.error("Failed to save note:", err);
      setSaveStatus("error");
    }
  };

  const handleChange = (value: string) => {
    setContent(value);
    setSaveStatus("idle");
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveNote(value);
    }, 1500);
  };

  if (isLoading) {
    return <div className="p-4">Loading…</div>;
  }

  const statusText: Record<SaveStatus, string> = {
    idle: "",
    saving: "Saving…",
    saved: "Saved",
    error: "Error saving",
  };

  const statusColor: Record<SaveStatus, string> = {
    idle: "text-transparent",
    saving: "text-gray-400",
    saved: "text-green-500",
    error: "text-red-500",
  };

  return (
    <div className="h-full w-full p-4 pt-2 flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-base font-semibold">Note — Comment #{id}</h1>
        <span className={`text-sm ${statusColor[saveStatus]}`}>
          {statusText[saveStatus] || "."}
        </span>
      </div>

      <div className="flex-1 min-h-0 mb-2">
        <ReactQuill
          className="h-full"
          theme="snow"
          value={content}
          onChange={handleChange}
          modules={modules}
          formats={formats}
        />
      </div>
    </div>
  );
}
